import { z } from 'zod';

import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodId } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodBrandedDateTimeString } from '@Common/types/zod/zod-branded-date';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';

import type { BoListQueryBase } from '@Common/types/bo/bo_list_query';

export const ZodScheduleInstrumentsType = z.enum(['mark_suspendable', 'mark_tradable']);
export const ZodScheduleInstrumentsStatus = z.enum(['pending', 'triggered', 'cancelled']);

export const ZodPostScheduleInstrumentsEditBody = z.object({
  instrument_id: ZodId,
  type: ZodScheduleInstrumentsType,
  scheduled_at: ZodBrandedDateTimeString,
});

export type PostScheduleInstrumentsEditBody = z.input<typeof ZodPostScheduleInstrumentsEditBody>;

export const ZodGetInstrumentsScheduledEditsItem = z.object({
  id: ZodId,
  instrument_id: ZodId,
  type: ZodScheduleInstrumentsType,
  scheduled_at: ZodBrandedDateTimeString,
  status: ZodScheduleInstrumentsStatus,
  created_at: ZodBrandedDateTimeString,
  updated_at: ZodBrandedDateTimeString,
});

export type GetInstrumentsScheduledEditsItem = z.TypeOf<typeof ZodGetInstrumentsScheduledEditsItem>;

export const getInstrumentsScheduledEditsSortColumns: (keyof GetInstrumentsScheduledEditsItem)[] = [
  'id',
  'instrument_id',
  'type',
  'scheduled_at',
  'status',
  'created_at',
  'updated_at',
];

const ZodGetInstrumentsScheduledEditsFilters = z
  .object({
    id: createZodArrayable(ZodId),
    instrument_id: createZodArrayable(ZodId),
    type: createZodArrayable(ZodScheduleInstrumentsType),
    status: createZodArrayable(ZodScheduleInstrumentsStatus),
  })
  .partial();

type GetInstrumentsScheduledEditsFilters = z.TypeOf<typeof ZodGetInstrumentsScheduledEditsFilters>;

export const ZodGetInstrumentsScheduledEditsQuery = buildZodBoListQueryParsed(
  ZodGetInstrumentsScheduledEditsFilters,
  getInstrumentsScheduledEditsSortColumns,
  {
    range: [0, 99],
    sort: ['scheduled_at', 'DESC'],
  },
);

export type GetInstrumentsScheduledEditsQuery = BoListQueryBase<
  GetInstrumentsScheduledEditsFilters,
  (typeof getInstrumentsScheduledEditsSortColumns)[number]
>;

const ZodGetInstrumentsScheduledEditsResponse = z.array(ZodGetInstrumentsScheduledEditsItem);

export const getInstrumentsScheduledEditsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetInstrumentsScheduledEditsQuery,
  responseBody: ZodGetInstrumentsScheduledEditsResponse,
});

export type GetInstrumentsScheduledEditsEndpoint = GetEndpointType<
  typeof getInstrumentsScheduledEditsEndpoint
>;

export const ZodGetInstrumentsScheduledEditsIdParams = createZodParamsValidator(
  z.object({
    id: ZodId,
  }),
);

export const getInstrumentsScheduledEditsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodGetInstrumentsScheduledEditsIdParams,
  responseBody: ZodGetInstrumentsScheduledEditsItem,
});

export type GetInstrumentsScheduledEditsIdEndpoint = GetEndpointType<
  typeof getInstrumentsScheduledEditsIdEndpoint
>;

export const putInstrumentsScheduledEditsCancelIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodGetInstrumentsScheduledEditsIdParams,
  responseBody: z.object({
    id: ZodId,
  }),
});

export type PutInstrumentsScheduledEditsCancelIdEndpoint = GetEndpointType<
  typeof putInstrumentsScheduledEditsCancelIdEndpoint
>;

export const postScheduleInstrumentsEditEndpoint = endpoint({
  method: 'POST',
  body: ZodPostScheduleInstrumentsEditBody,
  responseBody: z.object({ id: ZodId }),
});

export type PostScheduleInstrumentsEditEndpoint = GetEndpointType<
  typeof postScheduleInstrumentsEditEndpoint
>;
